<template>
  <v-combobox
    v-model="customerSelect"
    v-model:search="searchInput"
    v-model:menu="menuOpen"
    :disabled="disabled"
    :loading="isLoading"
    :items="availableCustomers"
    :label="$t('common.action.searchCustomers')"
    :hide-no-data="!showNoDataMessage"
    :no-data-text="$t('page.customer.noCustomersFound')"
    :prepend-inner-icon="disabled ? 'mdi-lock-outline' : 'mdi-magnify'"
    return-object
    no-filter
    item-title="name"
    item-value="id"
    hide-details
    :menu-props="{ closeOnContentClick: true }"
    @update:search="handleSearchInput"
    @update:model-value="handleSelection"
  >
    <template #item="{ item, props }">
      <v-list-item
        v-bind="{ ...props, title: undefined, subtitle: undefined }"
        :key="getCustomerItem(item).id ?? props?.value"
        @click="(event) => handleItemClick(props, event)"
      >
        <v-list-item-title>
          {{ getCustomerItem(item).name || getCustomerItem(item).id }}
        </v-list-item-title>
        <v-list-item-subtitle>
          <template v-if="getCustomerItem(item).email || getCustomerItem(item).phone">
            <span v-if="getCustomerItem(item).email">{{ getCustomerItem(item).email }}</span>
            <span v-if="getCustomerItem(item).email && getCustomerItem(item).phone"> • </span>
            <span v-if="getCustomerItem(item).phone">{{ getCustomerItem(item).phone }}</span>
          </template>
          <template v-else>{{ getCustomerItem(item).id }}</template>
        </v-list-item-subtitle>
      </v-list-item>
    </template>
  </v-combobox>
</template>

<script lang="ts" setup>
import { computed, nextTick, ref, watchEffect } from "vue";
import { useAuthStore } from "@/auth/stores/auth";
import { useSnackBarStore } from "@/shared/stores/snackbar";
import { AccountPlan } from "@/shared/enums/status.enum";
import type { Customer } from "@/customers/types/customer";
import { useCustomerSearch } from "@/customers/composables/useCustomerSearch";

const customerSelect = ref<Customer | null>(null);
const searchInput = ref("");
const menuOpen = ref(false);
const disabled = computed(() => useAuthStore().accountPlan === AccountPlan.STARTER);
const emit = defineEmits(["customer-selected"]);
const {
  results,
  isLoading,
  hasSearched,
  handleSearchInput: runSearchInput,
  clearSearch,
} = useCustomerSearch({
  pageSize: 10,
  onError: (message: string) => {
    useSnackBarStore().toastError({
      message,
    });
  },
});
const availableCustomers = computed(() => results.value);
const showNoDataMessage = computed(
  () => !isLoading.value && hasSearched.value && searchInput.value.trim().length > 0,
);

watchEffect(() => {
  const trimmedSearch = searchInput.value.trim();
  if (!trimmedSearch) {
    menuOpen.value = false;
    return;
  }

  menuOpen.value =
    isLoading.value || availableCustomers.value.length > 0 || showNoDataMessage.value;
});

const handleSearchInput = (value: string | null) => {
  searchInput.value = value ?? "";
  const searchTerm = searchInput.value.trim();
  if (!searchTerm) {
    clearSearch();
    return;
  }

  runSearchInput(searchTerm);
};

const handleSelection = (value: Customer | string | null) => {
  if (!value || typeof value === "string") {
    return;
  }

  emit("customer-selected", value);
  resetSelection();
};

const handleItemClick = (props?: { onClick?: (event?: Event) => void }, event?: Event) => {
  if (typeof props?.onClick === "function") {
    props.onClick(event);
  }
};

const getCustomerItem = (item: Customer | { raw?: Customer }) =>
  ("raw" in item && item.raw ? item.raw : item) as Customer;

const resetSelection = () => {
  nextTick(() => {
    customerSelect.value = null;
    searchInput.value = "";
    menuOpen.value = false;
    clearSearch();
  });
};
</script>
